"use client";

import { useState, useEffect } from "react";
import { Check, X, User, FileText, Eye, AlertCircle } from "lucide-react";
import MainServices from "../../../../services/MainServices";
import { notifyWarn } from "../../../../assets/elements/Toast";
import { useTranslation } from "react-i18next";

// Professional Data Percentage Cell Component
export const DataPercentageCell = ({ value, row, onViewSummary }) => {
  const percentage = Number.parseInt(value) || 0;
  const { t, i18n } = useTranslation();

  const getStatusColor = (percent) => {
    if (percent >= 90) return "text-gray-700";
    if (percent >= 75) return "text-gray-600";
    if (percent >= 50) return "text-gray-500";
    return "text-gray-500";
  };

  return (
    <div
      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors group"
      onClick={() => onViewSummary(row?.original?.personalInfo)}
    >
      <div className="flex items-center space-x-3">
        <span className={`text-sm font-medium ${getStatusColor(percentage)}`}>
          {percentage}%
        </span>
        <div className="w-16 bg-gray-200 rounded-sm h-1.5">
          <div
            className="bg-gray-600 h-1.5 rounded-sm transition-all duration-300"
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      </div>
      <Eye className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors ml-2" />
    </div>
  );
};

// Professional Employee Registration Summary Modal// Professional Employee Registration Summary Modal
export const EmployeeRegistrationSummaryModal = ({
  isOpen,
  onClose,
  detailsSummaryPublicId = "",
  handleViewProfile = () => {},
  handleEdit = () => {},
}) => {
  const { fetchEmployeeRegistrationDataById } = MainServices();
  const [isLoading, setIsLoading] = useState(false);
  const [employeeRegistrationData, setEmployeeRegistrationData] =
    useState(null);
  const [personalInfo, setPersonalInfo] = useState(null);
  const [employmentInfo, setEmploymentInfo] = useState(null);
  const [nationalIdDetails, setNationalIdDetails] = useState(null);
  const [salaryDetails, setSalaryDetails] = useState(null);
  const [iqamaDetails, setIqamaDetails] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  const [healthInsuranceDetails, setHealthInsuranceDetails] = useState(null);
  const [healthCardDetails, setHealthCardDetails] = useState(null);
  const [passportDetails, setPassportDetails] = useState(null);
  const [visaDetails, setVisaDetails] = useState(null);
  const [contractDetails, setContractDetails] = useState(null);
  const [drivingLicenseDetails, setDrivingLicenseDetails] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [dependentsInfo, setDependentsInfo] = useState(null);
  const [isCitizen, setIsCitizen] = useState(false);
  const [docsTabs, setDocsTabs] = useState([]);
  const { t } = useTranslation();

  // Initialize document tabs
  useEffect(() => {
    setDocsTabs([
      t("Salary Details"),
      t("Iqama"),
      t("National ID"),
      t("Account"),
      t("Health Insurance"),
      t("Health Card"),
      t("Passport"),
      t("Visa"),
      t("Contract"),
      t("Driving License"),
      t("Subscription"),
    ]);
  }, [t]);

  // Document sections configuration with key mapping
  const getDocumentSectionsConfig = () => [
    { key: "personalInfo", label: t("Personal Info"), dataField: personalInfo },
    {
      key: "employementInfo",
      label: t("Employment Info"),
      dataField: employmentInfo,
    },
    { key: "salaryInfo", label: t("Salary Details"), dataField: salaryDetails },
    { key: "iqamaInfo", label: t("Iqama"), dataField: iqamaDetails },
    {
      key: "nationalIdInfo",
      label: t("National ID"),
      dataField: nationalIdDetails,
    },
    { key: "bankAccountInfo", label: t("Account"), dataField: accountDetails },
    {
      key: "healthInfo",
      label: t("Health Insurance"),
      dataField: healthInsuranceDetails,
    },
    {
      key: "healthCardInfo",
      label: t("Health Card"),
      dataField: healthCardDetails,
    },
    { key: "passportInfo", label: t("Passport"), dataField: passportDetails },
    { key: "visaInfo", label: t("Visa"), dataField: visaDetails },
    { key: "contractInfo", label: t("Contract"), dataField: contractDetails },
    {
      key: "drivingInfo",
      label: t("Driving License"),
      dataField: drivingLicenseDetails,
    },
    {
      key: "subscriptionInfo",
      label: t("Subscription"),
      dataField: subscriptionDetails,
    },
    {
      key: "dependentInfo",
      label: t("Dependent Info"),
      dataField: dependentsInfo,
    },
  ];

  const getFilteredDocsTabs = (citizenStatus) => {
    const allSections = getDocumentSectionsConfig();

    if (citizenStatus === "true" || citizenStatus === true) {
      // For Saudi citizens - exclude Iqama, Visa, and Dependents from main calculation
      return allSections.filter(
        (section) =>
          section.key !== "iqamaInfo" &&
          section.key !== "visaInfo" &&
          section.key !== "dependentInfo"
      );
    } else {
      // For non-Saudi citizens - exclude National ID and Dependents from main calculation
      return allSections.filter(
        (section) =>
          section.key !== "nationalIdInfo" && section.key !== "dependentInfo"
      );
    }
  };

  const checkSectionCompletion = (sectionData) => {
    if (!sectionData || typeof sectionData !== "object") return false;

    // Handle arrays (like dependents)
    if (Array.isArray(sectionData)) {
      return sectionData.length > 0;
    }

    // Handle objects
    const values = Object.values(sectionData);
    return values.some((value) => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "object" && value !== null) {
        return Object.keys(value).length > 0;
      }
      return value !== null && value !== undefined && value !== "";
    });
  };

  // 2. Add this new function to get dependents status separately
  const getDependentsStatus = () => {
    const dependentsSection = getDocumentSectionsConfig().find(
      (section) => section.key === "dependentInfo"
    );
    return {
      section: dependentsSection,
      isCompleted: checkSectionCompletion(dependentsSection.dataField),
    };
  };

  const dependentsStatus = getDependentsStatus();

  // Your actual API integration
  const getEmployeeRegistrationData = async (empPublicId) => {
    setIsLoading(true);
    try {
      const response = await fetchEmployeeRegistrationDataById(empPublicId);
      if (response?.data) {
        const completeData = response?.data?.data;
        setEmployeeRegistrationData(completeData);
        setPersonalInfo(completeData?.personalInfo);
        setIsCitizen(completeData?.personalInfo?.citizen == "true");
        setEmploymentInfo(completeData?.employementInfo);
        setNationalIdDetails(completeData?.nationalIdInfo);
        setSalaryDetails(completeData?.salaryInfo);
        setIqamaDetails(completeData?.iqamaInfo);
        setAccountDetails(completeData?.bankAccountInfo);
        setHealthInsuranceDetails(completeData?.healthInfo);
        setHealthCardDetails(completeData?.healthCardInfo);
        setPassportDetails(completeData?.passportInfo);
        setVisaDetails(completeData?.visaInfo);
        setContractDetails(completeData?.contractInfo);
        setDrivingLicenseDetails(completeData?.drivingInfo);
        setSubscriptionDetails(completeData?.subscriptionInfo);
        setDependentsInfo(completeData?.dependentInfo || null);
        setIsLoading(false);
      }
    } catch (res) {
      if (notifyWarn) {
        notifyWarn(res?.message);
      }
      console.log(res);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && detailsSummaryPublicId) {
      getEmployeeRegistrationData(detailsSummaryPublicId);
    }
  }, [isOpen, detailsSummaryPublicId]);

  if (!isOpen) return null;

  const citizenStatus = personalInfo?.citizen;
  const filteredSections = getFilteredDocsTabs(citizenStatus);

  const completedSections = filteredSections.filter((section) =>
    checkSectionCompletion(section.dataField)
  );

  const incompleteSections = filteredSections.filter(
    (section) => !checkSectionCompletion(section.dataField)
  );

  const completionPercentage =
    filteredSections.length > 0
      ? Math.round((completedSections.length / filteredSections.length) * 100)
      : 0;

  return (
    <div className="fixed inset-0 bg-black/30 bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <h2 className="text-base font-bold leading-5 text-left text-[#04080b]">
                  {t("Registration Status")}
                </h2>
                <p className="text-sm font-medium leading-4 text-left text-[#04080b] mt-1">
                  {`${personalInfo?.firstName} ${personalInfo?.lastName}` ||
                    t("Employee Profile Review")}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-600"></div>
              <span className="ml-3 text-sm font-medium leading-4 text-left text-[#475467]">
                {t("Loading employee information...")}
              </span>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Status Overview */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-base font-bold leading-5 text-left text-[#04080b]">
                      {t("Profile Completion")}
                    </h3>
                    <p className="text-sm font-medium leading-4 text-left text-[#04080b] mt-1">
                      {t("Employee Type:")}{" "}
                      <span className="text-sm font-medium leading-4 text-left text-[#04080b]">
                        {isCitizen
                          ? t("Saudi National")
                          : t("Non-Saudi National")}
                      </span>
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-[#04080b]">
                      {completionPercentage}%
                    </div>
                    <div className="text-sm font-medium leading-4 text-left text-[#475467]">
                      {t("Complete")}
                    </div>
                  </div>
                </div>

                <div className="w-full bg-gray-200 rounded-sm h-2 mb-4">
                  <div
                    className="bg-gray-700 h-2 rounded-sm transition-all duration-500"
                    style={{ width: `${completionPercentage}%` }}
                  ></div>
                </div>

                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-lg font-bold text-[#04080b]">
                      {completedSections.length}
                    </div>
                    <div className="text-xs font-bold uppercase tracking-wide text-[#475467]">
                      {t("Completed")}
                    </div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-[#04080b]">
                      {incompleteSections.length}
                    </div>
                    <div className="text-xs font-bold uppercase tracking-wide text-[#475467]">
                      {t("Pending")}
                    </div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-[#04080b]">
                      {filteredSections.length}
                    </div>
                    <div className="text-xs font-bold uppercase tracking-wide text-[#475467]">
                      {t("Total")}
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-gray-100 rounded-md border border-gray-200">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">{t("Note:")}</span>{" "}
                      {t(
                        "The completion percentage is calculated based on whether each section contains any data, not on the completion of individual fields within sections. Dependents information is shown separately below and not included in the main calculation."
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Sections Status */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Completed Sections */}
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <Check className="w-5 h-5 text-gray-600" />
                    <h4 className="text-sm font-medium leading-4 text-left text-[#04080b]">
                      {t("Completed Sections")} ({completedSections.length})
                    </h4>
                  </div>

                  <div className="space-y-2">
                    {completedSections.length > 0 ? (
                      completedSections.map((section) => (
                        <div
                          key={section.key}
                          className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded"
                        >
                          <span className="text-sm font-medium leading-4 text-left text-[#04080b]">
                            {section.label}
                          </span>
                          <Check className="w-4 h-4 text-gray-500" />
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500 bg-gray-50 rounded">
                        <AlertCircle className="w-5 h-5 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm font-medium leading-4 text-left text-[#475467]">
                          {t("No sections completed yet")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Incomplete Sections */}
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <AlertCircle className="w-5 h-5 text-gray-600" />
                    <h4 className="text-sm font-medium leading-4 text-left text-[#04080b]">
                      {t("Pending Sections")} ({incompleteSections.length})
                    </h4>
                  </div>

                  <div className="space-y-2">
                    {incompleteSections.length > 0 ? (
                      incompleteSections.map((section) => (
                        <div
                          key={section.key}
                          className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded"
                        >
                          <span className="text-sm font-medium leading-4 text-left text-[#475467]">
                            {section.label}
                          </span>
                          <X className="w-4 h-4 text-gray-400" />
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500 bg-white border border-gray-200 rounded">
                        <Check className="w-5 h-5 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm font-medium leading-4 text-left text-[#475467]">
                          {t("All sections completed")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center space-x-2 mb-4">
                  <User className="w-5 h-5 text-gray-600" />
                  <h4 className="text-sm font-medium leading-4 text-left text-[#04080b]">
                    {t("Dependents Information")}
                  </h4>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded">
                    <span className="text-sm font-medium leading-4 text-left text-[#04080b]">
                      {dependentsStatus.section.label}
                    </span>
                    {dependentsStatus.isCompleted ? (
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-green-600">
                          {t("Completed")}
                        </span>
                        <Check className="w-4 h-4 text-green-500" />
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-500">
                          {t("Optional")}
                        </span>
                        <AlertCircle className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-xs text-blue-700">
                      <span className="font-medium">{t("Info:")}</span>{" "}
                      {t("Dependents information is optional.")}
                    </p>
                  </div> */}
                </div>
              </div>

              {/* Required Documents Notice */}
              {incompleteSections.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <FileText className="w-5 h-5 text-gray-500 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium leading-4 text-left text-[#04080b] mb-1">
                        {t("Action Required")}
                      </h5>
                      <p className="text-sm font-medium leading-4 text-left text-[#475467]">
                        {t(
                          "Please complete the pending sections to finalize the employee registration process."
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-bold leading-4 text-left text-[#475467] bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              {t("Close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
